import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Minus, Plus, X, Truck } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const resolveImg = (img) =>
  img?.startsWith("http") ? img : `${API}/files/${img}`;

const SHIP_COUNTRIES = [
  { code: "CA", label: "Canada" },
  { code: "US", label: "United States" },
  { code: "INTL", label: "International" },
];

export default function Cart() {
  const { items, update, remove, total: localSubtotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [shipTo, setShipTo] = useState("CA");

  useEffect(() => {
    if (items.length === 0) { setQuote(null); return; }
    const payload = { ship_to_country: shipTo, items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant_id: i.variant_id || null })) };
    api.post("/checkout/quote", payload).then(({ data }) => setQuote(data)).catch(() => setQuote(null));
  }, [items, shipTo]);

  const checkout = async () => {
    if (!user) { navigate("/login", { state: { from: "/cart" } }); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        origin_url: window.location.origin,
        ship_to_country: shipTo,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant_id: i.variant_id || null })),
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const subtotal = quote ? quote.subtotal : localSubtotal;
  const shipping = quote ? quote.shipping_total : 0;
  const total = quote ? quote.total : localSubtotal;

  return (
    <div data-testid="cart-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-4">◦ Your bag</div>
      <h1 className="font-serif text-4xl lg:text-5xl tracking-tight mb-12">
        {items.length === 0 ? <>Quiet for now.</> : <>{items.length} {items.length === 1 ? "piece" : "pieces"} chosen.</>}
      </h1>
      {items.length === 0 ? (
        <div>
          <p className="text-muted-foreground mb-8">Wander the studios — every piece has a maker.</p>
          <Link to="/shop" className="btn-primary inline-block px-7 py-3.5 text-sm tracking-wide rounded-sm">Visit the shop</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 divide-y divide-border/40">
            {items.map((i) => (
              <div key={i.key} data-testid={`cart-row-${i.product_id}`} className="py-6 flex gap-6 items-center">
                <div className="w-24 h-28 bg-secondary overflow-hidden rounded-sm flex-shrink-0">
                  <img src={resolveImg(i.image)} alt={i.title} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <Link to={`/product/${i.product_id}`} className="font-serif text-xl hover:text-primary transition-colors">{i.title}</Link>
                  {i.variant_label && <div className="text-xs text-muted-foreground mt-0.5" data-testid="cart-variant-label">{i.variant_label}</div>}
                  <div className="text-sm text-muted-foreground mt-1">${Number(i.price).toFixed(2)}</div>
                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex items-center border border-border rounded-sm">
                      <button onClick={() => update(i.key, i.quantity - 1)} className="p-2 hover:bg-secondary"><Minus className="w-3 h-3" /></button>
                      <span className="px-3 text-sm w-6 text-center">{i.quantity}</span>
                      <button onClick={() => update(i.key, i.quantity + 1)} className="p-2 hover:bg-secondary"><Plus className="w-3 h-3" /></button>
                    </div>
                    <button onClick={() => remove(i.key)} className="text-xs text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1">
                      <X className="w-3 h-3" /> Remove
                    </button>
                  </div>
                </div>
                <div className="font-serif text-xl">${(i.price * i.quantity).toFixed(2)}</div>
              </div>
            ))}
          </div>
          <aside className="lg:col-span-4 lg:pl-8">
            <div className="border border-border/40 p-8 rounded-sm sticky top-28 bg-card">
              <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-6">◦ Summary</div>
              <div className="flex justify-between text-sm mb-3">
                <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
              </div>

              {/* Ship-to country selector */}
              <div className="my-4 pt-3 border-t border-border/40">
                <label className="flex items-center gap-1.5 text-xs tracking-wider uppercase text-muted-foreground font-semibold mb-2">
                  <Truck className="w-3 h-3" /> Ship to
                </label>
                <Select value={shipTo} onValueChange={setShipTo}>
                  <SelectTrigger data-testid="ship-to-select" className="w-full bg-transparent border border-border rounded-sm px-3 py-2 h-auto text-sm focus:ring-0 focus:border-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SHIP_COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code} data-testid={`ship-to-option-${c.code}`}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Shipping breakdown per seller */}
              {quote && quote.breakdown.length > 0 && (
                <div className="my-4 pt-3 border-t border-border/40 space-y-2">
                  <div className="flex items-center gap-1.5 text-xs tracking-wider uppercase text-muted-foreground font-semibold">
                    <Truck className="w-3 h-3" /> Shipping
                  </div>
                  {quote.breakdown.map((b) => (
                    <div key={b.seller_id} className="flex justify-between items-baseline text-sm" data-testid={`shipping-${b.seller_id}`}>
                      <div>
                        <div className="text-muted-foreground italic">{b.seller_name}</div>
                        <div className="text-[10px] tracking-wider uppercase text-muted-foreground/80">{b.note}</div>
                      </div>
                      <span className={b.shipping === 0 ? "text-primary font-semibold" : ""}>
                        {b.shipping === 0 ? "Free" : `$${b.shipping.toFixed(2)}`}
                      </span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm pt-2 border-t border-border/30">
                    <span>Shipping total</span><span>{shipping === 0 ? <span className="text-primary font-semibold">Free</span> : `$${shipping.toFixed(2)}`}</span>
                  </div>
                </div>
              )}

              <div className="border-t border-border/60 pt-4 mt-4 flex justify-between items-baseline">
                <span className="text-sm">Total</span>
                <span className="font-serif text-3xl" data-testid="cart-total">${total.toFixed(2)}</span>
              </div>
              <button
                data-testid="checkout-btn"
                onClick={checkout}
                disabled={loading}
                className="btn-primary w-full mt-8 py-3.5 text-sm tracking-wide rounded-sm disabled:opacity-60"
              >
                {loading ? "Preparing…" : "Proceed to checkout"}
              </button>
              <p className="mt-4 text-xs text-muted-foreground text-center">Secure payment via Stripe</p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
